<template>
  <!-- Map -->
  <div ref="map_view" class="map-container w-100 vh-100"></div>

  <!-- Modal: Initial conditions -->
  <ModalInitialConditions
      ref="modalSteps"
      :prop-status="modals.initialConditions.status"
      :prop-show-update="modals.initialConditions.update"
      :prop-initial-values="modals.initialConditions.data"
      :prop-initial-step="modals.initialConditions.step"
      @on-click-open-info="aboutModalOpen"
      @on-click-open-feedback="feedbackOffCanvasOpen"
      @on-close-modal-click="initialConditionsModalClose"
      @on-click-save-data="initialConditionsOnClickSave"/>

  <!-- Modal: About -->
  <ModalInformation
      :prop-modal-status="modals.about.status"
      :prop-show-about-startup="modals.about.startup"
      :prop-show-section="modals.about.section"
      :prop-glossary-term="modals.about.term"
      @on-modal-about-close="aboutModalClose"/>

  <LayerInfo
      prop-id="info_box_content_simulation"
      :prop-layer-variable-configuration="simulationVariableConfiguration"
      :prop-month="simulationMonthLabel"
      :prop-show-click-message="this.loading.simulation === false"

      :prop-hover-water-infrastructure-object="controls.infoLayer.waterInfrastructureObject"
      :prop-layer-areas-selectable="layerVariableSelectable"
      :prop-layer-hover-feature-id="controls.infoLayer.layerAreaFeatureId"

      :prop-show-municipalities="controls.mapOptions.municipalities === true"
      :prop-toggle="controls.infoLayer.toggle"
      v-if="controlsShow.layerInfo === true"
  />

  <AboutButton
      @click="aboutModalOpen"
      prop-id="aboutButtonSimulation"
      :prop-show-tooltip="this.controls.about.tooltip"
      v-show="controlsShow.aboutButton"
  />

  <!-- Box: Legend -->
  <ColorLegend
      v-if="controlsShow.legendColor"
      :prop-lakes="counters.lakes"
      :prop-derivations="counters.derivations"
      :prop-derivation-points="counters.derivation_points"
      :prop-stations="counters.stations"
      :prop-lakes-natural="counters.lakes_natural"
      :prop-glaciers="counters.glaciers"
      :prop-rivers-show="controls.mapOptions.water_resources === true"
      :prop-glacier-show="controls.mapOptions.glaciers"
      :prop-lakes-show="controls.mapOptions.lakes"
      :prop-agriculture-area-show="controls.mapOptions.agriculture_areas"
      :prop-administrative-units-show="controls.mapOptions.municipalities === true"
      :prop-anthropic-works-show="controls.mapOptions.anthropic_works === true"
  />

  <!-- Box: Simulation inputs -->
  <SimulationInputs
      v-if="controlsShow.legendSimulation"
      :prop-simulation-initial-conditions="simulation.initialConditions"
      :prop-anthropic-works-updated="anthropicWorksUpdatedCount"
      :prop-anthropic-works-count="anthropicWorksTotal"
      :prop-show-tab-results="controlsShow.simulationAccordions.results"
      :prop-loading-run-simulation="loading.simulation"
      :prop-loading-explore="loading.explore"
      :prop-block-all="loading.timeseries"
      :prop-accordion-trigger-inputs="controls.legendSimulation.accordion.inputs"
      :prop-accordion-trigger-results="controls.legendSimulation.accordion.results"
      :prop-accordion-trigger-results-clear-form="controls.legendSimulation.accordion.results"
      :prop-accordion-trigger-hide="controls.legendSimulation.accordion.hide"
      :prop-variables="variableMap"

      :prop-simulation-id="simulation.id"
      :prop-simulation-month="simulation.month"
      :prop-uuid-simulation-results="UUIDLast.simulationResults"
      :prop-block-run-simulation="simulationRunButtonDisabled"

      @on-click-show-initialize-toast="anthropicWorksToastClose"
      @on-edit-input-click="initialConditionsOnClickOpenModalInStep"
      @on-click-run-simulation="simulationOnClickRun"
      @on-click-explore="simulationResultsOnClickExplore"
      @tooltip-glossary-on-click="tooltipGlossaryOnClick"
  />

  <AnthropicWorksModal
      prop-modal-id="modalAnthropicWorks"
      :prop-status="modals.anthropicWorks.status"
      :prop-model="modals.anthropicWorks.model"
      @on-modal-click-close="anthropicWorksModalOnClickClose"
      @on-click-save-changes="anthropicWorkOnUpdate"
      @on-click-reset-values="anthropicWorkReset"
  />

  <!-- Time-series buttons -->
  <TimeSeriesControl
      prop-id="time-series-control-simulation"
      :prop-locale="$i18n.locale"
      :prop-tooltip-trigger-show="controls.buttonsTimeSeries.triggers.show"
      v-if="controlsShow.buttonsTimeSeries === true"
      :prop-loading-data="loading.timeseries"
      :prop-buttons-multiple-select="layerVariableMultipleSelect"
      :prop-button-data-disabled="buttonTimeSeriesDataDisabled"
      :prop-button-select-all-disabled="buttonTimeSeriesSelectAllDisabled"
      :prop-button-clear-selection-disabled="buttonTimeSeriesClearSelectionDisabled"
      @on-data-click="timeSeriesButtonClick"
      @on-clear-selection-click="timeSeriesButtonClearSelectionClick"
      @on-select-all-click="timeSeriesButtonSelectAllClick"
  />

  <Legend prop-id="legend_content_simulation"
          :prop-unit="this.controlLegendUnit"
          :prop-legend-color-artifact="controlLegendColorArtifact"
          v-if="controlLegendColorArtifact !== null"
  />

  <MapOptions prop-id="mapOptionsSimulation"
              :prop-options="mapOptions"
              @on-switch-change="mapLayerOptionsOnSwitch"
              :prop-disabled="loading.explore === true || loading.simulation === true"
              v-if="controls.mapOptions.status === true"/>

  <!-- Tooltip content for water infrastructure items on mouse over -->
  <div class="d-none" ref="tooltipWater">
    <div class="row">
      <div class="col text-center" style="font-size: 0.9rem">
        <div class="fw-semibold">{{ controls.tooltipWater.name }}</div>
        <div class="badge rounded-pill me-1" :class="anthropicWorkBadgeColorClass(controls.tooltipWater.object_type)">
          {{ controls.tooltipWater.object_type_name }}
        </div>
      </div>
    </div>
  </div>

  <!-- Plot -->
  <PlotSimulation
      :prop-variable="simulationVariableConfiguration"
      :prop-simulation-data="simulation.variable"
      :prop-plot-layers="controls.timeSeriesPlot.layersSelected"
      :prop-plot-layer-names="controls.timeSeriesPlot.layersSelectedNames"
      :prop-plot-layers-count="controls.timeSeriesPlot.layersCount"
      :prop-plot-layers-trigger="controls.timeSeriesPlot.trigger"
      :prop-modal-status="modals.plot.status"
      @on-open-modal="timeSeriesPlotOnOpen"
      @on-close-modal-click="timeSeriesPlotOnClose"
      @after-plot-data-loading="timeSeriesModalAfterDataLoading"
      @on-time-series-data-error="timeSeriesModalOnErrorLoading"
  />

  <!-- TUTORIAL toasts -->
  <!-- Tutorial: step for initial conditions -->
  <ToastTip prop-toast-id="toastMessageStepInitialConditions"
            prop-toast-position="middle"
            :prop-toast-delay="15000"
            :prop-toast-trigger="toasts.initialConditions.status"
            :prop-title="$t('SimulationInputs.GettingStarted')"
            :prop-storage-flag="constants.STORAGE.SHOW_SIMULATION_STEP_INITIAL_CONDITIONS"
            @on-toast-close="initialConditionsToastClose"
            @on-toast-accept="initialConditionsToastAccept">
    <p v-html="$t('SimulationInformation.initialConditionsStep')"></p>
  </ToastTip>

  <!-- Tutorial: step for anthropic works -->
  <ToastTip prop-toast-id="toastMessageStepAnthropicWorks"
            prop-toast-position="middle"
            :prop-toast-delay="15000"
            :prop-toast-trigger="toasts.anthropicWorks.status"
            :prop-title="$t('SimulationInputs.AnthropicWorks')"
            :prop-storage-flag="constants.STORAGE.SHOW_SIMULATION_STEP_ANTHROPIC_WORKS"
            @on-toast-close="anthropicWorksToastClose"
            @on-toast-accept="anthropicWorksToastAccept">
    <p v-html="$t('SimulationInformation.anthropicWorksStepModify1')"></p>
    <p v-html="$t('SimulationInformation.anthropicWorksStepModify2')"></p>
  </ToastTip>

  <!-- Tutorial: step for simulation results -->
  <ToastTip prop-toast-id="toastMessageStepResults"
            prop-toast-position="middle"
            :prop-toast-delay="15000"
            :prop-toast-trigger="toasts.simulationResults.status"
            :prop-title="$t('SimulationInputs.SimulationResults')"
            :prop-storage-flag="constants.STORAGE.SHOW_SIMULATION_STEP_SIMULATION_RESULTS"
            @on-toast-close="simulationResultsToastClose"
            @on-toast-accept="simulationResultsToastAccept">
    <p v-html="$t('SimulationInformation.simulationResultsStep1')"></p>
    <p v-html="$t('SimulationInformation.simulationResultsStep2')"></p>
  </ToastTip>

  <!-- Messages -->
  <Toast
      prop-id="toastApiError"
      :prop-trigger="toasts.messages.api.status"
      :prop-auto-hide=true
      prop-type="danger"
      @on-toast-hidden="() => {toasts.messages.api.status = false;}">
    <template v-slot:body>
      <span v-html="$t(toasts.messages.api.message)"></span>
    </template>
  </Toast>

  <Toast
      prop-id="toastSimulationSuccess"
      :prop-trigger="toasts.messages.success.status"
      :prop-auto-hide=true
      prop-type="success"
      @on-toast-hidden="() => {toasts.messages.success.status = false;}">
    <template v-slot:body>
      <span v-html="$t('SimulationInformation.simulationComplete')"></span>
    </template>
  </Toast>

  <Toast
      prop-id="toastSimulationError"
      :prop-trigger="toasts.messages.error.status"
      :prop-auto-hide=true
      prop-type="danger"
      @on-toast-hidden="() => {toasts.messages.error.status = false;}">
    <template v-slot:body>
      <span v-html="$t('SimulationInformation.simulationError')"></span>
    </template>
  </Toast>

</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Storage from "@/libs/Storage";
import constants from "@/libs/Constants";
import Legend from '@/components/Legend.vue';
import TimeSeriesControl from "@/components/TimeSeriesControl.vue";
import LayerInfo from '@/views/Simulation/MapContents/LayerInfo.vue';
import ModalInformation from '@/views/Simulation/Modals/Information.vue';

// Map Comp
import {toRaw} from "vue";
import Toast from "@/components/Toast.vue";
import ToastTip from "@/components/ToastTip.vue";
import AboutButton from "@/components/AboutButton.vue";
import LegendColor from '@/components/LegendColor.vue';
import SimulationInputs from '@/views/Simulation/MapContents/SimulationInputs.vue';
import ModalInitialConditions from '@/views/Simulation/Modals/ModalInitialConditions.vue';

import {
  flagStoreStepToasts,
  flagRegisteredEmail,
  flagSimulationMaxAttempts,
  flagSimulationCheckTimeout,
  UUIDfromList, getWaterInfrastructureTypeI18NLabel, anthropicWorkBadgeColorClass
} from '@/libs/Helpers';
import AnthropicWorksModal from "@/views/Simulation/Modals/AnthropicWorks.vue";
import {
  SimulationAnthropicWorksDamLake,
  SimulationAnthropicWorksDerivation,
  SimulationAnthropicWorksHydroPlant,
  SimulationVariable
} from "@/libs/SimulationData";

import LoginStorage from '@/libs/Login';
import ApiConnection from "@/libs/ApiConnection";
import {VariableConfiguration} from "@/libs/VisualizationData";
import {VariableColorConfigurationInterface} from "@/libs/Colors";
import PlotSimulation from "@/views/Simulation/Modals/PlotSimulation.vue";
import GroupErrorTranslation from "@/libs/locales/errors";
import MapOptions from "@/components/MapOptions.vue";
import {
  LayerAreasSelectable,
  layerMapOSMLoad,
  layerLoadWrapper,
  layerSimulationLoad,
  mapLayerWaterStructureStyle,
  layerDerivationCreateDirectionArrows
} from "@/libs/Layers";

export default {
  name: 'MapView',
  emits: ['onOpenFeedbackClick', 'onAboutButtonClick', 'onModalOpenClose'],
  props: {
    propFeedbackStatus: Boolean,
    propModalAboutClose: Boolean,
    propModalInitialConditionsClose: Boolean,
    propModalAnthropicWorksClose: Boolean,
    propModalPlotClose: Boolean,
  },
  components: {
    MapOptions,
    PlotSimulation,
    TimeSeriesControl,
    AboutButton,
    ToastTip,
    Toast,
    AnthropicWorksModal,
    Legend, // Add this line
    LayerInfo,
    ModalInformation,
    ModalInitialConditions,
    ColorLegend: LegendColor,
    SimulationInputs,
  },
  watch: {
    propModalAboutClose(newValue, oldValue) {
      this.modals.initialConditions.previous = false;
      this.modals.initialConditions.shownFirstTime = true;
      this.aboutModalSetStatus(false);
    },
    propModalInitialConditionsClose(newValue, oldValue) {
      this.modals.initialConditions.previous = false;
      this.modals.initialConditions.shownFirstTime = true;
      this.initialConditionsModalSetStatus(false);
    },
    propModalAnthropicWorksClose(newValue, oldValue) {
      this.modals.initialConditions.previous = false;
      this.modals.initialConditions.shownFirstTime = true;
      this.anthropicWorksModalSetStatus(false);
    },
    propModalPlotClose(newValue, oldValue) {
      this.modals.initialConditions.previous = false;
      this.modals.initialConditions.shownFirstTime = true;
      this.timeSeriesModalSetStatus(false);
    },
    propFeedbackStatus(newValue, oldValue) {
      if (newValue === false) {
        if (this.modals.initialConditions.previous === true)
          this.initialConditionsModalOpen();
      }
    }
  },
  data() {
    return {
      constants,
      modals: {
        initialConditions: {
          status: false, // Show/hide modal with steps
          previous: false,
          update: false, //Update mode
          step: null,
          data: null,
          shownFirstTime: false
        },
        about: {
          status: false,
          startup: false,
          section: constants.INFORMATION_OPTIONS.SUMMARY,
          term: null
        },
        anthropicWorks: {
          status: false,
          model: null
        },
        plot: {
          status: false,
        }
      },

      toasts: {
        initialConditions: {
          status: false,
          shown: false,
          accepted: false
        },
        anthropicWorks: {
          status: false,
          shown: false,
          accepted: false
        },
        simulationResults: {
          status: false,
          shown: false,
          accepted: false,
        },
        messages: {
          error: {
            status: false,
            message: ''
          },
          success: {
            status: false
          },
          api: {
            status: false,
            message: ''
          }
        }
      },

      simulation: {
        initialConditions: null, //Initial conditions to generate simulation
        anthropicWorks: {}, //Anthropic works to generate simulation

        id: null, //ID of simulation after running
        variable: null, //Variable from simulation results
        month: null, //Month for simulation results

        maxAttempts: flagSimulationMaxAttempts(),
        checkTimeout: flagSimulationCheckTimeout()
      },

      controlsShow: {
        layerInfo: false,
        legendColor: false,
        legendSimulation: false,
        legendAreaColor: false,
        anthropicWorks: false,
        aboutButton: false,
        simulationAccordions: {
          results: false
        },
        buttonsTimeSeries: false,
      },

      controls: {
        map: null, // map object
        about: {
          tooltip: false,
        },
        legendColor: {
          unit: '',
          minValue: null,
          maxValue: null,
          colorScale: null
        },
        infoLayer: {
          model: null, //instance to retrieve data
          toggle: false,
          waterInfrastructureObject: null,
          layerAreaFeatureId: null
        },
        legendSimulation: {
          accordion: {
            inputs: false,
            results: false,
            hide: false
          },
        },
        layerVariable: {
          selectable: null,
          selectionCount: 0
        },
        buttonsTimeSeries: {
          triggers: {
            show: false,
            hide: false
          }
        },
        timeSeriesPlot: {
          layersSelected: null,
          layersSelectedNames: [],
          layersCount: null,
          trigger: false
        },
        mapOptions: {
          status: false,
          grayscale: false,
          anthropic_works: true,
          glaciers: true,
          lakes: true,
          water_resources: true,
          agriculture_areas: false,
          municipalities: false
        },
        tooltipWater: {
          name: null,
          object_type: null,
          object_type_name: null
        },
        scale: null //Scale object
      },

      counters: {
        lakes: 0,
        derivations: 0,
        derivation_points: 0,
        stations: 0,
        rivers: 0,
        lakes_natural: 0,
        glaciers: 0
      },

      layersMap: {},

      loading: {
        simulation: false,
        explore: false,
        timeseries: false
      },

      //Available variables
      variableMap: {},

      UUIDLast: {
        simulationConditions: null,
        simulationResults: null
      }
    };
  },
  computed: {
    anthropicWorksUpdatedCount() {
      let updatedCount = 0;
      Object.keys(this.simulation.anthropicWorks).forEach((type) => {
        Object.keys(this.simulation.anthropicWorks[type]).forEach(id => {
          let modelObj = this.simulation.anthropicWorks[type][id];
          if (modelObj.hasDefaultValues === false)
            updatedCount++;
        });
      });
      return updatedCount;
    },
    anthropicWorksTotal() {
      let totalCount = 0;
      Object.keys(this.simulation.anthropicWorks).forEach((type) => {
        Object.keys(this.simulation.anthropicWorks[type]).forEach(id => {
          let modelObject = this.simulation.anthropicWorks[type][id];

          //Count only editable features
          if (modelObject.editable === true)
            totalCount++;
        });
      });
      return totalCount;
    },
    simulationAnthropicWorksInitialConditionsUUID() {
      let anthropicWorksUUIDs = [];
      Object.keys(this.simulation.anthropicWorks).sort().forEach((type) => {
        Object.keys(this.simulation.anthropicWorks[type]).sort().forEach(id => {
          anthropicWorksUUIDs.push(this.simulation.anthropicWorks[type][id].uuid);
        });
      });

      let finalUUID = null;
      if (anthropicWorksUUIDs.length > 0 && this.simulation.initialConditions !== null) {
        finalUUID = UUIDfromList([
              this.simulation.initialConditions.uuid,
              UUIDfromList(anthropicWorksUUIDs)
            ]
        )
      }
      return finalUUID;
    },
    simulationRunButtonDisabled() {
      return this.UUIDLast.simulationConditions !== null &&
          this.UUIDLast.simulationConditions === this.simulationAnthropicWorksInitialConditionsUUID;
    },
    simulationVariableConfiguration() {
      return this.simulation.variable !== null ? this.variableMap[this.simulation.variable.variable_id] : null;
    },
    simulationMonthLabel() {
      return this.simulation.variable !== null ?
          this.simulation.variable.monthLabel(this.$i18n, 'long') : '';
    },
    layerVariableSelectable() {
      return this.controls.layerVariable.selectable;
    },
    layerVariableCountTotalFeatures() {
      return this.layerVariableSelectable !== null ?
          this.layerVariableSelectable.layersCount :
          0;
    },
    layerVariableSelectionCount() {
      return this.controls.layerVariable.selectionCount
    },
    layerVariableMultipleSelect() {
      return this.simulationVariableConfiguration !== null ? this.simulationVariableConfiguration.area_multiple_selection : false;
    },

    buttonTimeSeriesDataDisabled() {
      return this.layerVariableSelectionCount === 0 || this.loading.simulation === true || this.loading.explore === true;
    },
    buttonTimeSeriesSelectAllDisabled() {
      return (this.layerVariableCountTotalFeatures > 0 && this.layerVariableSelectionCount === this.layerVariableCountTotalFeatures) ||
          this.loading.simulation === true ||
          this.loading.explore === true ||
          this.loading.timeseries === true;
    },
    buttonTimeSeriesClearSelectionDisabled() {
      return this.layerVariableSelectionCount === 0 ||
          this.loading.simulation === true ||
          this.loading.explore === true ||
          this.loading.timeseries === true;
    },
    controlLegendUnit() {
      return this.simulationVariableConfiguration !== null ? this.simulationVariableConfiguration.unit : '';
    },
    controlLegendColorArtifact() {
      return this.layerVariableSelectable !== null ? this.layerVariableSelectable.legendColorArtifactObject : null;
    },
    mapOptions() {
      return [
        {
          id: constants.MAP_OPTIONS.MAP_GRAYSCALE,
          selected: false,
          label: this.$t('SimulationInputs.GrayscaleMap')
        },
        {
          id: constants.MAP_OPTIONS.WATER_RESOURCES,
          selected: true,
          label: this.$t('SimulationInputs.WaterResources')
        },
        {
          id: constants.MAP_OPTIONS.LAKES,
          selected: true,
          label: this.$t('SimulationInputs.Lakes')
        },
        {
          id: constants.MAP_OPTIONS.GLACIER,
          selected: true,
          label: this.$t('SimulationInputs.Glaciers')
        },
        {
          id: constants.MAP_OPTIONS.MUNICIPALITIES,
          selected: false,
          label: this.$t('SimulationInputs.Municipalities')
        },
        {
          id: constants.MAP_OPTIONS.AGRICULTURE_AREA,
          selected: false,
          label: this.$t('SimulationInputs.AgricultureAreas')
        },
        {
          id: constants.MAP_OPTIONS.ANTHROPIC_WORKS,
          selected: true,
          label: this.$t('SimulationInputs.AnthropicWorks')
        }
      ]
    }
  },
  methods: {
    anthropicWorkBadgeColorClass,
    /*********
     * About *
     *********/
    aboutModalSetStatus(status) {
      this.modals.about.status = status;
      this.$emit('onModalOpenClose', 'about', status);
    },
    aboutModalOpen() {
      this.anthropicWorksToastClose();
      this.controls.about.tooltip = false;
      this.aboutModalOpenOnSection(constants.INFORMATION_OPTIONS.SUMMARY);
    },
    aboutModalOpenOnSection(section) {
      this.modals.about.section = '';
      this.$nextTick(() => {
        this.modals.about.section = section;
        this.aboutModalSetStatus(true);
      });
    },
    aboutModalClose() {
      this.aboutModalSetStatus(false);
      if (this.modals.initialConditions.shownFirstTime === false)
        this.initialConditionsModalOpen();
    },
    aboutButtonTooltipShow() {
      this.controls.about.tooltip = true;
    },
    aboutButtonTooltipHide() {
      this.controls.about.tooltip = false;
    },

    feedbackOffCanvasOpen() {
      this.initialConditionsModalClose();
      this.modals.initialConditions.previous = true;
      this.$emit('onOpenFeedbackClick');
    },

    /**********************
     * Initial conditions *
     **********************/
    initialConditionsToastShow() {
      if (this.toasts.initialConditions.accepted === false && this.toasts.initialConditions.shown === false)
        setTimeout(() => {
          this.toasts.initialConditions.status = true;
          this.toasts.initialConditions.shown = true;
        }, 100);
    },
    initialConditionsToastClose() {
      this.toasts.initialConditions.status = false;
    },
    initialConditionsToastAccept() {
      this.initialConditionsToastClose();
      this.toasts.initialConditions.accepted = true;
    },
    initialConditionsModalSetStatus(status) {
      this.modals.initialConditions.status = status;
      this.$emit('onModalOpenClose', 'initialConditions', status);
    },
    initialConditionsModalOpen() {
      this.initialConditionsModalSetStatus(true);
      this.modals.initialConditions.previous = true;
      this.modals.initialConditions.shownFirstTime = true;
      this.initialConditionsToastShow();
    },
    initialConditionsModalClose() {
      this.modals.initialConditions.data = null;
      this.modals.initialConditions.step = null;
      this.initialConditionsModalSetStatus(false);
    },
    initialConditionsOnClickSave(initialConditionsData) {
      this.simulation.initialConditions = initialConditionsData;
      this.initialConditionsModalClose();
      this.anthropicWorksLayersShow();
      this.simulationCheckModifiedInputs();
    },
    initialConditionsOnClickOpenModalInStep(step) {
      this.anthropicWorksToastClose();
      this.modals.initialConditions.step = step;
      this.modals.initialConditions.data = this.simulation.initialConditions;

      //Change flag to true, modal now behaves as update
      this.modals.initialConditions.update = true;

      this.aboutButtonTooltipHide();
      this.initialConditionsModalOpen();
    },

    anthropicWorksToastShow() {
      if (this.toasts.anthropicWorks.accepted === false && this.toasts.anthropicWorks.shown === false)
        setTimeout(() => {
          this.toasts.anthropicWorks.status = true;
          this.toasts.anthropicWorks.shown = true;
        }, 100);
    },

    anthropicWorksToastClose() {
      this.initialConditionsToastClose();
      this.toasts.anthropicWorks.status = false;
    },
    anthropicWorksToastAccept() {
      this.anthropicWorksToastClose();
      this.toasts.anthropicWorks.accepted = true;
    },

    /*******************
     * Anthropic works *
     *******************/
    anthropicWorksModalSetStatus(status) {
      this.modals.anthropicWorks.status = status;
      this.$emit('onModalOpenClose', 'anthropicWorks', status);
    },
    anthropicWorkOnClickEdit(anthropicWorkData) {
      this.modals.anthropicWorks.model = anthropicWorkData;
      this.anthropicWorksModalSetStatus(true);
    },
    anthropicWorksModalOnClickClose() {
      this.anthropicWorksModalSetStatus(false);
    },
    anthropicWorkOnUpdate(anthropicWorkObject) {
      this.anthropicWorksAdd(anthropicWorkObject);

      //Close modal
      this.anthropicWorksModalSetStatus(false);

      this.simulationCheckModifiedInputs();
    },
    anthropicWorkReset(anthropicWorkObject) {
      this.anthropicWorksAdd(anthropicWorkObject);

      //Close modal
      this.anthropicWorksModalSetStatus(false);
    },
    anthropicWorksAdd(modelObject) {
      let objectType = modelObject.type;
      let objectId = modelObject.id;

      if (objectType !== null && objectType !== undefined && objectId !== null && objectId !== undefined) {
        if (this.simulation.anthropicWorks.hasOwnProperty(objectType) === false)
          this.simulation.anthropicWorks[objectType] = {};
        this.simulation.anthropicWorks[objectType][objectId] = modelObject;
      }
    },
    anthropicWorkGetById(type, id) {
      let typeItems = this.simulation.anthropicWorks[type];
      let workObject = typeItems !== undefined ? typeItems[id] : null;
      return workObject !== undefined ? workObject : null;
    },
    anthropicWorksGetSerializedItemsByType(type, editableOnly = true) {
      let serializedObjects = [];
      if (this.simulation.anthropicWorks.hasOwnProperty(type) === true)
        Object.keys(this.simulation.anthropicWorks[type]).forEach(id => {
          let modelObject = this.simulation.anthropicWorks[type][id];
          if (editableOnly === false || modelObject.editable === true)
            serializedObjects.push(modelObject.serialized);
        });

      return serializedObjects;
    },
    anthropicWorkOrRiverByGeoJSONFeature(feature, layerType) {
      let featureObject = null;

      switch (layerType) {
        case constants.LAYERS.DAM_LAKE:
        case constants.LAYERS.DERIVATION:
        case constants.LAYERS.PLANT:
          let featureId = feature.properties['id'];
          featureObject = this.anthropicWorkGetById(layerType, featureId);
          break;
      }
      return featureObject;
    },
    anthropicWorksLayersShow() {
      if (this.controlsShow.anthropicWorks === false) {
        //Map options button
        this.controls.mapOptions.status = true;

        //Box
        this.controlsShow.legendSimulation = true;

        //Colors
        this.controlsShow.legendColor = true;

        //Info box + layers
        if (this.controlsShow.layerInfo === false)
            //Load layers
          this.mapLayerReloadVisible();

        this.controlInfoLayerBoxAddOrReplace();

        //Anthropic works
        this.controlsShow.anthropicWorks = true;
        this.anthropicWorksToastShow();

        //About button
        this.controlsShow.aboutButton = true;
        setTimeout(this.aboutButtonTooltipShow, 0);
      }
    },

    /*******
     * Map *
     *******/
    controlScaleAddOrReplace() {
      if (this.controls.scale) {
        this.controls.map.removeControl(this.controls.scale);
        this.controls.scale = null;
      }
      this.controls.scale = L.control.scale();
      this.controls.scale.addTo(toRaw(this.controls.map));
    },

    controlInfoLayerBoxAddOrReplace() {
      this.controls.infoLayer.value = null;
      this.controls.infoLayer.area = null;
      this.controlsShow.layerInfo = true;
    },
    controlZoomButtonAddOrReplace() {
      if (this.controlZoom) {
        this.controls.map.removeControl(this.controlZoom);
        this.controlZoom = null;
      }
      this.controlZoom = L.control.zoom({
        position: 'bottomleft'
      });
      this.controlZoom.addTo(toRaw(this.controls.map));
    },
    toastMessageApiTrigger(error) {
      this.toasts.messages.api.message = GroupErrorTranslation.t(error.message, this.$i18n);
      this.toasts.messages.api.status = true;
    },
    toastMessageErrorTrigger() {
      this.toasts.messages.error.status = true;
    },
    toastMessageSuccessTrigger() {
      this.toasts.messages.success.status = true;
    },
    mapOSMLayer(grayscale = false) {
      return layerMapOSMLoad(9, 15, grayscale);
    },
    mapLayerHoverableOnEachFeature(feature, layer, featureNameKey, featureType) {
      layer.on('mouseover', () => {
        this.controls.tooltipWater.name = feature.properties[featureNameKey];
        this.controls.tooltipWater.object_type = featureType;
        this.controls.tooltipWater.object_type_name = this.$t(getWaterInfrastructureTypeI18NLabel(featureType));

        this.$nextTick(() => {
          layer.bindTooltip(this.$refs.tooltipWater.innerHTML).openTooltip();
        });
      });
    },
    mapLayerGlacierOnEachFeature(feature, layer) {
      this.mapLayerHoverableOnEachFeature(feature, layer, 'name', constants.LAYERS.GLACIER);
    },
    mapLayerLakeOnEachFeature(feature, layer) {
      this.mapLayerHoverableOnEachFeature(feature, layer, 'name', constants.LAYERS.LAKES);
    },
    mapLayerDerivationPointOnEachFeature(feature, layer) {
      this.mapLayerHoverableOnEachFeature(feature, layer, 'name', constants.LAYERS.DERIVATION_POINTS);
    },
    mapLayerHydroelectricPlantOnEachFeature(feature, layer) {
      this.mapLayerHoverableOnEachFeature(feature, layer, 'Nome', constants.LAYERS.PLANT);
    },
    async mapInitialize() {
      this.controls.map = L.map(this.$refs.map_view, {zoomControl: false}).setView([46.052926, 10.798974], 10);

      this.mapLayerOSMLoad();

      this.controls.map.attributionControl.setPosition('bottomleft');
      this.controlScaleAddOrReplace();
      this.controlZoomButtonAddOrReplace();

      //Create objects for fixed layers
      this.mapLayerObjectsCreate();
    },
    mapLayerVariableOnSelectedLayersChange(count) {
      //Force LayerInfo component to refresh
      this.controls.infoLayer.toggle = !this.controls.infoLayer.toggle;

      //Update number of selected areas
      this.controls.layerVariable.selectionCount = count;
    },
    mapLayerVariableOnAreaMouseOver(layerFeatureId) {
      if (this.simulationVariableConfiguration !== null)
        this.controls.infoLayer.layerAreaFeatureId = layerFeatureId;
    },
    mapLayerVariableOnAreaMouseOut() {
      this.controls.infoLayer.layerAreaFeatureId = null;
    },

    /**
     * Load variable data and keep it in list of layers
     * @param variableConfiguration {VariableConfiguration}
     * @param data
     */
    mapLayerVariableLoad(variableConfiguration, data) {
      this.mapLayerVariableClear();

      let layerSelectable = new LayerAreasSelectable(
          data,
          variableConfiguration.unit,
          variableConfiguration.decimals,
          variableConfiguration.palette,
          variableConfiguration.area_multiple_selection,
          null,
          this.mapLayerVariableOnAreaMouseOver,
          this.mapLayerVariableOnAreaMouseOut,
          this.mapLayerVariableOnSelectedLayersChange
      );

      this.controls.layerVariable.selectionCount = 0;
      this.controls.layerVariable.selectable = layerSelectable;

      this.controls.infoLayer.layerAreaFeatureId = null;
      this.controls.infoLayer.toggle = !this.controls.infoLayer.toggle;

      let layerVariable = layerSelectable.layerLeaflet;
      layerVariable.addTo(toRaw(this.controls.map));

      this.layersMap[constants.LAYERS.VARIABLE] = layerVariable
    },
    mapLayerObjectLoadGeoJsonClickableForModal(layerData, layerType, featureClickable = true) {
      let layerObjectStyleConfig = mapLayerWaterStructureStyle(layerType);

      let layerOptions = {
        onEachFeature: (feature, layer) => {
          layer.on('mouseover', () => {
            let featureObject = this.anthropicWorkOrRiverByGeoJSONFeature(feature, layerType);
            if (featureObject !== null) {
              layer.setStyle(layerObjectStyleConfig.mouseOverStyle);
              this.controls.infoLayer.waterInfrastructureObject = featureObject;
            }
          });

          layer.on('mouseout', () => {
            layer.setStyle(layerObjectStyleConfig.mouseOutStyle);
            this.controls.infoLayer.waterInfrastructureObject = null;
          });

          if (featureClickable === true)
            layer.on('click', () => {
              let featureObject = this.anthropicWorkOrRiverByGeoJSONFeature(feature, layerType);
              if (featureObject !== null && featureObject.editable === true && this.loading.simulation === false)
                this.anthropicWorkOnClickEdit(featureObject.clone());
            });
        }
      }

      if (layerObjectStyleConfig.styleFunction !== null)
        layerOptions.style = layerObjectStyleConfig.styleFunction;

      if (layerObjectStyleConfig.stylePointFunction !== null)
        layerOptions.pointToLayer = (feature, latLong) => {
          return layerObjectStyleConfig.stylePointFunction(latLong)
        };

      //Add layer
      let layerObject = L.geoJSON(layerData, layerOptions);

      return {
        object: layerObject,
        count: layerData.features.length
      };
    },
    mapLayerObjectsCreate() {
      let hoverableMapFunction = {
        [constants.LAYERS.LAKES]: this.mapLayerLakeOnEachFeature,
        [constants.LAYERS.GLACIER]: this.mapLayerGlacierOnEachFeature,
        [constants.LAYERS.DERIVATION_POINTS]: this.mapLayerDerivationPointOnEachFeature,
        [constants.LAYERS.PLANT]: this.mapLayerHydroelectricPlantOnEachFeature
      }

      /* ------------------------------
       * Load GeoJSON layers to the map
       * ------------------------------ */
      let layerPromises = [];

      let layerKeys = [
        constants.LAYERS.AREA,
        constants.LAYERS.RIVER,
        constants.LAYERS.MUNICIPALITIES,
        constants.LAYERS.GLACIER,
        constants.LAYERS.LAKES,
        constants.LAYERS.AGRICULTURE_AREA,
        constants.LAYERS.DERIVATION_DIRECTION,
        constants.LAYERS.DERIVATION_POINTS
      ];

      let layerCounterMap = {
        [constants.LAYERS.LAKES]: 'lakes_natural',
        [constants.LAYERS.GLACIER]: 'glaciers',
        [constants.LAYERS.DERIVATION_POINTS]: 'derivation_points'
      };

      let layerWaterKeys = [
        constants.LAYERS.DERIVATION,
        constants.LAYERS.DAM_LAKE,
        constants.LAYERS.PLANT
      ];

      layerKeys.forEach(layer => {
        if (hoverableMapFunction.hasOwnProperty(layer))
          layerPromises.push(layerLoadWrapper(layer, true, hoverableMapFunction[layer]));
        else
          layerPromises.push(layerLoadWrapper(layer, true));
      });

      layerWaterKeys.forEach(layer => {
        layerPromises.push(layerSimulationLoad(layer));
      })

      Promise.allSettled(layerPromises).then((values) => {
        let dataIndex = 0;
        //Fixed layers: keep them in list
        layerKeys.forEach(layerKey => {
          let layerData = values[dataIndex];
          if (layerData.status === "fulfilled" && layerData.value !== null) {
            let layerResult = layerData.value;
            let layerLeaflet = layerResult.object;

            this.layersMap[layerKey] = layerLeaflet;

            // Add arrowheads to the line
            if (layerKey === constants.LAYERS.DERIVATION_DIRECTION)
              this.layersMap[constants.LAYERS.DERIVATION_DIRECTION_ARROW] = layerDerivationCreateDirectionArrows(layerLeaflet);

            if (layerCounterMap.hasOwnProperty(layerKey)) {
              let counterKey = layerCounterMap[layerKey];
              this.counters[counterKey] = layerResult.count;
            }
          }

          dataIndex += 1;
        });


        /**
         * Load layers for anthropic works in the desired order
         */
        let layerItems = [
          //Derivations
          {
            type: constants.LAYERS.DERIVATION,
            clickable: true,
            counter: 'derivations'
          },
          //Dam / lake
          {
            type: constants.LAYERS.DAM_LAKE,
            clickable: true,
            counter: 'lakes'
          },
          //Hydropower plants
          {
            type: constants.LAYERS.PLANT,
            clickable: true,
            counter: 'stations'
          }
        ];

        dataIndex = layerKeys.length;
        layerItems.forEach(layerConfiguration => {
          //Get Promise object based on order
          let indexOfLayer = layerWaterKeys.indexOf(layerConfiguration.type)
          if (indexOfLayer === -1)
            return;

          let layerDataPromise = values[dataIndex + indexOfLayer];

          if (layerDataPromise.status === "fulfilled" && layerDataPromise.value !== null) {
            let layerData = layerDataPromise.value;
            let layerConfigurationType = layerConfiguration.type;

            if (layerConfigurationType === constants.LAYERS.PLANT) {
              /* ------------------------------
               * Water structures: not editable
               * ------------------------------*/
              let layerObjectStyleConfig = mapLayerWaterStructureStyle(layerConfigurationType);

              let layerOptions = {
                onEachFeature: hoverableMapFunction[layerConfigurationType]
              };

              if (layerObjectStyleConfig.styleFunction !== null)
                layerOptions.style = layerObjectStyleConfig.styleFunction;

              if (layerObjectStyleConfig.stylePointFunction !== null)
                layerOptions.pointToLayer = (feature, latLong) => {
                  return layerObjectStyleConfig.stylePointFunction(latLong)
                };

              //Add layer
              this.layersMap[layerConfigurationType] = L.geoJSON(layerData, layerOptions);
              this.counters[layerConfiguration.counter] = layerData.features.length;

            } else {
              /* --------------------------
               * Water structures: editable
               * --------------------------*/
              let layerObjectConfig = this.mapLayerObjectLoadGeoJsonClickableForModal(
                  layerData,
                  layerConfigurationType,
                  layerConfiguration.clickable
              );

              this.layersMap[layerConfigurationType] = layerObjectConfig.object;
              this.counters[layerConfiguration.counter] = layerObjectConfig.count;

              //Initialize data for water infrastructures
              layerData.features.forEach(feature => {
                let featureId = feature.properties['id'];
                let featureType = feature.properties['Tipologia'];
                let featureName = feature.properties['Nome'];
                let featureEditable = feature.properties.hasOwnProperty('editable') ?
                    feature.properties['editable'] :
                    true;

                let featureObject = null;
                switch (featureType) {
                  case constants.LAYERS.RAW_DAM_LAKE:
                    featureObject = new SimulationAnthropicWorksDamLake(featureId, featureName, featureEditable);
                    break;
                  case constants.LAYERS.RAW_DERIVATION:
                    featureObject = new SimulationAnthropicWorksDerivation(featureId, featureName, featureEditable);
                    break;
                  case constants.LAYERS.RAW_PLANT:
                    featureObject = new SimulationAnthropicWorksHydroPlant(featureId, featureName, featureEditable);
                    break;
                }

                if (featureObject !== null)
                  this.anthropicWorksAdd(featureObject);
              });
            }
          }
        });
      });
    },
    mapLayerReloadByKeys(layerKeys) {
      layerKeys.forEach(layerKey => {
        if (this.layersMap.hasOwnProperty(layerKey) === true) {
          let layerObject = this.layersMap[layerKey];

          if (this.controls.map.hasLayer(layerObject))
            this.controls.map.removeLayer(layerObject);
          layerObject.addTo(toRaw(this.controls.map));
        }
      })
    },
    mapLayerRemoveByKey(layerKey, deleteLayer = false) {
      if (this.layersMap.hasOwnProperty(layerKey) === true) {
        let layerObject = this.layersMap[layerKey];

        if (this.controls.map.hasLayer(layerObject)) {
          this.controls.map.removeLayer(layerObject);

          if (deleteLayer === true)
            delete this.layersMap[layerKey];
        }
      }
    },
    mapLayerWaterResourcesClear() {
      this.mapLayerRemoveByKey(constants.LAYERS.RIVER);
    },
    mapLayerMunicipalitiesClear() {
      this.mapLayerRemoveByKey(constants.LAYERS.MUNICIPALITIES);
    },
    mapLayerVariableClear() {
      this.mapLayerRemoveByKey(constants.LAYERS.VARIABLE, true);
    },
    mapLayerOSMLoad() {
      this.layersMap[constants.LAYERS.MAP] = this.mapOSMLayer(this.controls.mapOptions.grayscale);
      this.mapLayerReloadByKeys([constants.LAYERS.MAP]);
    },
    mapLayerReloadVisible() {
      this.mapLayerOSMLoad();

      //Area
      this.mapLayerReloadByKeys([constants.LAYERS.AREA]);

      //Municipalities
      if (this.controls.mapOptions.municipalities === true)
        this.mapLayerReloadByKeys([constants.LAYERS.MUNICIPALITIES]);
      else
        this.mapLayerMunicipalitiesClear();

      //Rivers
      if (this.controls.mapOptions.water_resources === true)
        this.mapLayerReloadByKeys([constants.LAYERS.RIVER]);
      else
        this.mapLayerWaterResourcesClear();

      //Variable
      this.mapLayerReloadByKeys([constants.LAYERS.VARIABLE]);

      //Background layers
      let mapOptionsKeys = {
        'glaciers': constants.LAYERS.GLACIER,
        'lakes': constants.LAYERS.LAKES,
        'agriculture_areas': constants.LAYERS.AGRICULTURE_AREA
      };

      Object.keys(mapOptionsKeys).forEach(layerKey => {
        let layerId = mapOptionsKeys[layerKey];

        if (this.controls.mapOptions[layerKey] === true)
          this.mapLayerReloadByKeys([layerId]);
        else
          this.mapLayerRemoveByKey(layerId);
      });

      let layerKeys = [
        constants.LAYERS.DERIVATION,
        constants.LAYERS.DERIVATION_POINTS,
        constants.LAYERS.DERIVATION_DIRECTION,
        constants.LAYERS.DERIVATION_DIRECTION_ARROW,
        constants.LAYERS.DAM_LAKE,
        constants.LAYERS.PLANT
      ];

      if (this.controls.mapOptions.anthropic_works === true)
        this.mapLayerReloadByKeys(layerKeys);
      else {
        layerKeys.forEach(layerKey => {
          this.mapLayerRemoveByKey(layerKey);
        })
      }
    },
    mapLayerOptionsOnSwitch(optionId, value) {
      let optionMap = {
        [constants.MAP_OPTIONS.MAP_GRAYSCALE]: 'grayscale',
        [constants.MAP_OPTIONS.WATER_RESOURCES]: 'water_resources',
        [constants.MAP_OPTIONS.MUNICIPALITIES]: 'municipalities',
        [constants.MAP_OPTIONS.ANTHROPIC_WORKS]: 'anthropic_works',
        [constants.MAP_OPTIONS.LAKES]: 'lakes',
        [constants.MAP_OPTIONS.GLACIER]: 'glaciers',
        [constants.MAP_OPTIONS.AGRICULTURE_AREA]: 'agriculture_areas'
      };

      if (optionMap.hasOwnProperty(optionId)) {
        let optionMapped = optionMap[optionId];
        this.controls.mapOptions[optionMapped] = value;
        this.mapLayerReloadVisible();
      }
    },

    /**************
     * Simulation *
     **************/
    simulationCheckModifiedInputs() {
      if (this.UUIDLast.simulationConditions !== null && this.simulationRunButtonDisabled === false)
        this.controls.legendSimulation.accordion.inputs = !this.controls.legendSimulation.accordion.inputs;
    },
    simulationOnClickRun() {
      this.anthropicWorksToastClose();

      //API call to run simulation and waiting until the simulation is ready
      this.loading.simulation = true;

      let initialConditions = this.simulation.initialConditions.serialized;
      let derivationItems = this.anthropicWorksGetSerializedItemsByType(constants.LAYERS.DERIVATION);
      let damLakeItems = this.anthropicWorksGetSerializedItemsByType(constants.LAYERS.DAM_LAKE);

      ApiConnection.postSimulationRun(
          initialConditions.month,
          initialConditions.snow_water_percentile,
          initialConditions.precipitation_temperature_type,
          initialConditions.precipitation_temperature_percentile,
          initialConditions.soil_water_percentile,
          derivationItems,
          damLakeItems
      ).then(response => {
        response.json().then(data => {
          let simulationId = data.id;

          this.simulation.id = simulationId;
          this.simulation.month = this.simulation.initialConditions.month;
          this.UUIDLast.simulationResults = null;

          setTimeout(() => {
                this.simulationCheckStatus(simulationId)
              }, 3000
          );
        });
      }).catch((error) => {
        //Show error
        this.toastMessageApiTrigger(error);
        this.loading.simulation = false;
      });
    },
    simulationCheckStatus(id, attempts = 0) {
      let simulationMaxAttempts = this.simulation.maxAttempts;
      let simulationCheckTimeout = this.simulation.checkTimeout;

      // Check current status in API
      attempts += 1;
      ApiConnection.getSimulationStatus(id).then(response => {
        response.json().then(data => {
          switch (data.status) {
            case constants.SIMULATION_STATUS.OK:
              // Finish check, show success and stop simulation waiting
              this.simulationOnStatusDone();
              break;
            case constants.SIMULATION_STATUS.STALLED:
              // Finish check, show error and stop simulation waiting
              this.simulationOnStatusError();
              break;
            case constants.SIMULATION_STATUS.RUNNING:
            default:
              // If attempts <= 5 -> check again
              if (attempts <= simulationMaxAttempts)
                setTimeout(() => {
                      this.simulationCheckStatus(id, attempts);
                    }, simulationCheckTimeout
                );
              else
                this.simulationOnStatusError(); // Finish check, show error and stop simulation waiting

              break;
          }
        });
      }).catch(error => {
        // If attempts <= 5 -> check again
        if (attempts <= simulationMaxAttempts)
          setTimeout(() => {
                this.simulationCheckStatus(id, attempts);
              }, simulationCheckTimeout
          );
        else
          this.simulationOnStatusError(); // Finish check, show error and stop simulation waiting
      });
    },
    simulationOnStatusDone() {
      // -- If OK --
      //Show accordion of results with month
      this.controlsShow.simulationAccordions.results = true;
      this.controls.legendSimulation.accordion.results = !this.controls.legendSimulation.accordion.results;

      //Show alert
      this.toastMessageSuccessTrigger();

      //Clear previous simulation data
      this.simulation.variable = null;
      this.controls.timeSeriesPlot.layersSelected = null;
      this.controls.timeSeriesPlot.layersCount = null;

      this.mapLayerVariableClear();
      this.timeSeriesButtonsHide();
      this.legendAreaHide();
      this.simulationResultsToastShow();

      this.UUIDLast.simulationConditions = this.simulationAnthropicWorksInitialConditionsUUID;

      this.loading.simulation = false;
    },
    simulationOnStatusError() {
      this.toastMessageErrorTrigger();
      this.loading.simulation = false;
    },

    /*******
     * API *
     *******/
    simulationGetVariables(showError = true) {
      ApiConnection.getSimulationLayers().then(result => {
        result.json().then(output => {
          let variables = output.variables;

          variables.forEach(item => {
            let itemId = item.id;

            let itemColorScaleData = item.color_scale;

            let colorInterface = new VariableColorConfigurationInterface();
            let variableColorPalette = colorInterface.newObject(itemColorScaleData.type, itemColorScaleData.values);

            this.variableMap[itemId] = new VariableConfiguration(
                itemId,
                {
                  en: item.name.en,
                  it: item.name.it,
                },
                variableColorPalette,
                item.measure_unit_abbreviation,
                item.decimals || 0,
                item.allows_multiple_areas
            );
          });
        });
      }).catch(error => {
        if (showError) {
          //TODO: how to download variables if no data is available?
        }
      }).finally(() => {

      });
    },
    simulationResultsToastShow() {
      if (this.toasts.simulationResults.accepted === false && this.toasts.simulationResults.shown === false) {
        this.toasts.simulationResults.status = true;
        this.toasts.simulationResults.shown = true;
      }
    },
    simulationResultsToastClose() {
      this.anthropicWorksToastClose();
      this.toasts.simulationResults.status = false;
    },
    simulationResultsToastAccept() {
      this.simulationResultsToastClose();
      this.toasts.simulationResults.accepted = true;
    },
    simulationResultsOnClickExplore(simulationData) {
      //Close toast if open
      this.toasts.simulationResults.status = false;

      let simulation_id = this.simulation.id;
      let variable_id = simulationData.variable;
      let forecastMonth = simulationData.month;

      //Call API to retrieve map
      this.loading.explore = true;

      ApiConnection.postSimulationQuery(simulation_id, variable_id, forecastMonth).then(result => {
        result.json().then(data => {
          //Hide all accordions from the simulation box
          this.controls.legendSimulation.accordion.hide = !this.controls.legendSimulation.accordion.hide;
          this.simulation.variable = new SimulationVariable(simulation_id, variable_id, forecastMonth);

          //Load variable
          this.mapLayerVariableLoad(this.simulationVariableConfiguration, data);

          //Reload all layers
          this.mapLayerReloadVisible();

          //Show time series buttons
          this.timeSeriesButtonsShow();

          //Show legend for areas
          this.legendAreaShow();

          this.UUIDLast.simulationResults = UUIDfromList([simulation_id, variable_id, forecastMonth]);
        });
      }).catch((error) => {
        //Show error
        this.toastMessageApiTrigger(error);

      }).finally(() => {
        //Enable button
        this.loading.explore = false;
      });
    },
    timeSeriesButtonsShow() {
      this.controlsShow.buttonsTimeSeries = true;
      this.controls.buttonsTimeSeries.triggers.show = !this.controls.buttonsTimeSeries.triggers.show;
    },
    timeSeriesButtonsHide() {
      this.controls.buttonsTimeSeries.triggers.hide = !this.controls.buttonsTimeSeries.triggers.hide;
      this.controlsShow.buttonsTimeSeries = false;
    },
    timeSeriesButtonClick() {
      this.loading.timeseries = true;

      //Set selected layers to plot modal
      if (this.controls.layerVariable.selectable !== null && this.controls.layerVariable.selectable !== undefined) {
        this.controls.timeSeriesPlot.layersSelected = this.controls.layerVariable.selectable.layersSelectedIdsSorted;
        this.controls.timeSeriesPlot.layersSelectedNames = this.controls.layerVariable.selectable.layerSelectedNameList;
        this.controls.timeSeriesPlot.layersCount = this.controls.layerVariable.selectable.layersCount;
      } else {
        this.controls.timeSeriesPlot.layersSelected = null;
        this.controls.timeSeriesPlot.layersSelectedNames = [];
        this.controls.timeSeriesPlot.layersCount = null;
      }

      //Trigger modal to load data
      this.controls.timeSeriesPlot.trigger = !this.controls.timeSeriesPlot.trigger;
    },
    timeSeriesModalSetStatus(status) {
      this.modals.plot.status = status;
      this.$emit('onModalOpenClose', 'plot', status);
    },
    timeSeriesPlotOnOpen(modalName) {
      this.timeSeriesModalSetStatus(true);
    },
    timeSeriesPlotOnClose(modalName) {
      this.timeSeriesModalSetStatus(false);
    },
    timeSeriesButtonSelectAllClick() {
      if (this.layerVariableSelectable !== null && this.layerVariableCountTotalFeatures > 0)
        this.layerVariableSelectable.layersSelectAll();
    },
    timeSeriesButtonClearSelectionClick() {
      if (this.layerVariableSelectable !== null)
        this.layerVariableSelectable.layersClearAll();
    },
    timeSeriesModalAfterDataLoading() {
      this.loading.timeseries = false;
    },
    timeSeriesModalOnErrorLoading(error) {
      //Show error
      this.toastMessageApiTrigger(error);

      this.loading.timeseries = false;
    },
    legendAreaShow() {
      this.controlsShow.legendAreaColor = true;
    },
    legendAreaHide() {
      this.controlsShow.legendAreaColor = false;
    },
    tooltipGlossaryOnClick(variable) {
      this.aboutModalSetStatus(false);
      this.aboutModalOpenOnSection(constants.INFORMATION_OPTIONS.GLOSSARY);
      this.modals.about.term = variable;
    },
  },

  /* --------
   * Mounting
     -------- */
  mounted() {
    this.mapInitialize();
    //this.anthropicWorksLayersShow();
  },
  beforeMount() {
    if (flagRegisteredEmail() === true && LoginStorage.emailRegistered === null)
      return false;

    if (flagStoreStepToasts() === true) {
      //Retrieve items for accepted toasts
      let toastItemShowMap = {
        'initialConditions': constants.STORAGE.SHOW_SIMULATION_STEP_INITIAL_CONDITIONS,
        'anthropicWorks': constants.STORAGE.SHOW_SIMULATION_STEP_ANTHROPIC_WORKS,
        'simulationResults': constants.STORAGE.SHOW_SIMULATION_STEP_SIMULATION_RESULTS
      }

      Object.keys(toastItemShowMap).forEach(toastKey => {
        let storageKey = toastItemShowMap[toastKey];
        let shownValue = Storage.get(storageKey);
        this.toasts[toastKey].accepted = shownValue !== null ? shownValue : false;
      })
    }

    let labelStorage = constants.STORAGE.SHOW_DSS_ABOUT_STARTUP;
    let showAbout = Storage.get(labelStorage);

    if (showAbout === null) {
      Storage.set(labelStorage, true);
      this.modals.about.startup = true;
    } else {
      this.modals.about.startup = showAbout;
    }

    this.aboutModalSetStatus(this.modals.about.startup);

    if (this.modals.about.status === false) {
      this.initialConditionsModalOpen();
    }

    this.simulationGetVariables();
  }
};
</script>